import { Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { faAdd, faChevronRight, faCircleExclamation, faCopy, faFileDownload, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { catchError } from 'rxjs';
import { IError } from 'src/app/models/error.model';
import { IFormStructure } from 'src/app/models/form.interface';
import { IModule } from 'src/app/models/module.model';
import { ITemplate } from 'src/app/models/template.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { DocumentsService } from 'src/app/services/documents.service';
import { DownloadService } from 'src/app/services/download.service';
import { ModulesService } from 'src/app/services/modules.service';
import { TemplatesService } from 'src/app/services/templates.service';

@Component({
  selector: 'app-template-form',
  templateUrl: './template-form.component.html',
  styleUrls: ['./template-form.component.sass']
})
export class TemplateFormComponent implements OnInit {

  faChevronRight = faChevronRight;
  faCircleExclamation = faCircleExclamation;
  faTrash = faTrash;
  faAdd = faAdd;
  faCopy = faCopy;
  faFileDownload = faFileDownload;

  templateId: string | null = '';

  modules: IModule[] = [];

  allFields: IFormStructure[] = [];
  // Listas filtradas para busca
  allFieldsFiltered: IFormStructure[] = [];
  selectedAllFields: IFormStructure[] = [];
  selectedFields: IFormStructure[] = [];
  selectedFieldsFiltered: IFormStructure[] = [];
  selectedSelectedFields: IFormStructure[] = [];
  loading = false;
  isEditPage = false;
  uploadedFileName = '';
  selectedFile?: File;

  // Termos de busca
  availableFieldsSearch: string = '';
  selectedFieldsSearch: string = '';

  templateForm = new UntypedFormGroup({
    name: new FormControl('', [Validators.required]),
    templateType: new FormControl("", [Validators.required]),
    module: new FormControl("", [Validators.required]),
    urlLink: new FormControl('', []),
    file: new FormControl('', []),
  })

  constructor(
    private documentsService: DocumentsService,
    private templatesService: TemplatesService,
    private modulesService: ModulesService,
    private router: Router, private route: ActivatedRoute,
    private store: Store<{ warningPopup: IError }>,
    private downloadService: DownloadService
  ) {
  }
  
  ngOnInit(): void {
    this.loading = true;
    this.route.paramMap.subscribe(params => {
      this.templateId = params.get('templateId');
    });
    this.modulesService.listModules()
      .subscribe((modules) => {
        this.modules = modules;
      });
    this.templatesService.listAllFields()
      .then((fields) => {
        this.allFields = fields;
        this.updateFieldsFilters();
      })
      .then(() => {
        if (this.router.url.includes('edit')) {
          this.isEditPage = true;
          this.getTemplate();
        }
        else {
          this.loading = false;
        }
      });
  }

  moveToSelected() {
    this.selectedFields.push(...this.selectedAllFields);
    this.selectedFields.sort((a, b) => a.order - b.order);
    this.allFields = this.allFields.filter(item => !this.selectedAllFields.includes(item));
    this.selectedAllFields = [];
    this.updateFieldsFilters();
  }

  moveToAvailable() {
    this.allFields.push(...this.selectedSelectedFields);
    this.allFields.sort((a, b) => a.order - b.order);
    this.selectedFields = this.selectedFields.filter(item => !this.selectedSelectedFields.includes(item));
    this.selectedSelectedFields = [];
    this.updateFieldsFilters();
  }

  cancelButton() {
    this.navigateToTemplatesList();
  }

  /**
   * Navega para a lista de templates
   */
  private navigateToTemplatesList(): void {
    this.router.navigate(['main', 'v2', 'templates']);
  }

  getTemplate() {
    if (this.templateId) {
      this.templatesService.getTemplateById(this.templateId)
        .then((template) => {
          this.templateForm.controls['name'].setValue(template.name)
          if (template.url_link) {
            this.templateForm.controls['urlLink'].setValue(template.url_link)
          }
          this.selectedFields = template.fields || [];
          this.selectedFields.forEach(field => {
            this.allFields = this.allFields.filter(f => f.id !== field.id);
          });
          this.updateFieldsFilters();
          this.templateForm.controls['module'].setValue(template.modules?.[0]?.id || "");
          this.templateForm.controls['templateType'].setValue(template.template_type?.toString() || "");
        })
        .then(() => {
          this.loading = false;
        });
    }
  }

  checkEmptyFields(objects: any[]): boolean {
    return objects.every(obj =>
      Object.values(obj).every(value => {
        if (value === undefined || value === '') {
          return false;
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return this.checkEmptyFields([value]);
        }
        return true;
      })
    );
  }

  changeTemplateFile(event: any) {
    const file = event.target.files[0];
    
    // Validar tipo de arquivo
    if (file) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/vnd.openxmlformats-officedocument.presentationml.presentation' // .pptx
      ];
      
      if (!allowedTypes.includes(file.type)) {
        this.store.dispatch(setWarningText("Por favor, selecione apenas arquivos .DOCX ou .PPTX"));
        return;
      }
    }
    
    this.selectedFile = file;
    this.templateForm.controls['file'].setValue(file);
    this.uploadedFileName = file.name;
  }

  removeTemplateFile() {
    this.templateForm.controls['file'].setValue('');
    this.selectedFile = undefined;
    this.uploadedFileName = '';
    // Reset the file input to allow re-selecting the same file
    const fileInput = document.getElementById('uploadTemplate') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // Atualiza listas filtradas baseado nos termos de busca atuais
  private updateFieldsFilters(): void {
    const norm = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const sAvail = norm(this.availableFieldsSearch);
    const sSel = norm(this.selectedFieldsSearch);

    this.allFieldsFiltered = this.allFields.filter(f =>
      !sAvail || norm(`${f.category} - ${f.name}`).includes(sAvail)
    );
    this.selectedFieldsFiltered = this.selectedFields.filter(f =>
      !sSel || norm(`${f.category} - ${f.name}`).includes(sSel)
    );
  }

  onAvailableFieldsSearchChange(value: string): void {
    this.availableFieldsSearch = value || '';
    this.updateFieldsFilters();
  }

  onSelectedFieldsSearchChange(value: string): void {
    this.selectedFieldsSearch = value || '';
    this.updateFieldsFilters();
  }

  /**
   * Faz download da versão atual do template sem cache
   */
  downloadCurrentTemplate() {
    const urlLink = this.templateForm.controls['urlLink'].value;
    if (!urlLink) {
      console.error('URL do template não encontrada');
      return;
    }

    // Determina o tipo do arquivo baseado na extensão da URL
    const url = urlLink.toLowerCase();
    let fileType = 'docx'; // padrão
    
    if (url.includes('.pdf')) {
      fileType = 'pdf';
    } else if (url.includes('.pptx')) {
      fileType = 'pptx';
    }

    // Nome do arquivo com timestamp para evitar conflitos
    const templateName = this.templateForm.controls['name'].value || 'template';
    const fileName = `${templateName}_atual_${new Date().getTime()}.${fileType}`;

    this.downloadService.downloadTemplate(urlLink, fileName, fileType);
  }


  submitProvider() {

    if (this.templateForm.invalid
      || this.checkEmptyFields([this.selectedFields])
      || (this.selectedFile === undefined && !this.isEditPage)) {
      this.templateForm.markAllAsTouched();
      this.store.dispatch(setWarningText("Preencha e valide todos os campos obrigatórios."));
      this.loading = false;
      return;
    }
    const formValue = this.templateForm.value;
    this.loading = true;

    let templateRequest: ITemplate = {
      name: formValue.name?.trim() || '',
      template_type: formValue.templateType?.trim() || '',
      fields: this.selectedFields,
      modules: [{ id: this.templateForm.controls['module'].value } as IModule],
      order: 999,
    }

    if (!this.isEditPage)
      this.templatesService.createTemplate(templateRequest, this.selectedFile)
        .pipe(
          catchError(error => {
            this.loading = false;
            return error;
          })
        )
        .subscribe((response) => {
          this.loading = false;
          this.navigateToTemplatesList();
        })
    else {
      this.templatesService.updateTemplate(this.templateId || '', templateRequest, this.selectedFile, this.templateForm.controls['module'].value)
        .subscribe((response) => {
          this.loading = false;
          this.navigateToTemplatesList();
        });
    }
  }
}
